import {
  type RidePassengerInfo,
  usePassengerAuthentications,
} from '@backoffice-frontend/ad-passenger-list'
import { PassengerAuthenticationState } from '@backoffice-frontend/graphql/subscription'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthenticationStatus } from '../PassengerCard/PassengerCard'
import type {
  AdVehicleStreamPassengerCardCustomerInfoFragment,
  AdVehicleStreamPassengerCardSeatInfoFragment,
  AdVehicleStreamPassengerCardStopInfoFragment,
} from '../PassengerCard/PassengerCard.hook'
import { BoardingPassengerCard } from './BoardingPassengerCard'
import { usePendingTimeout } from './usePendingTimeout'

const PassengerAuthenticationStateToAuthenticationStatus: Record<
  PassengerAuthenticationState,
  AuthenticationStatus
> = {
  [PassengerAuthenticationState.ManualNoShowRequested]:
    AuthenticationStatus.MANUAL_NO_SHOW_REQUESTED,
  [PassengerAuthenticationState.NoShowTriggered]:
    AuthenticationStatus.NO_SHOW_TRIGGERED,
  [PassengerAuthenticationState.InProgress]: AuthenticationStatus.IN_PROGRESS,
  [PassengerAuthenticationState.Confirmed]: AuthenticationStatus.CONFIRMED,
  [PassengerAuthenticationState.Cancelled]: AuthenticationStatus.CANCELLED,
  [PassengerAuthenticationState.Upcoming]: AuthenticationStatus.UPCOMING,
}

type BoardingPassengerWithAuthenticationProps = {
  tripId: string
  vehicleId: string
  customer: AdVehicleStreamPassengerCardCustomerInfoFragment | null
  ridePassengerInfo?: RidePassengerInfo | null
  waypointStop: AdVehicleStreamPassengerCardStopInfoFragment | null
  seats: AdVehicleStreamPassengerCardSeatInfoFragment
  eta: Date
  isBoarded: boolean
  isNoShow: boolean
}
export const BoardingPassengerCardWithAuthentication = ({
  tripId,
  vehicleId,
  customer,
  ridePassengerInfo,
  waypointStop,
  seats,
  eta,
  isBoarded,
  isNoShow,
}: BoardingPassengerWithAuthenticationProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { getPassengerAuthentication } = usePassengerAuthentications()
  const subscriptionStatus = getPassengerAuthentication(tripId)

  const mappedStatus = subscriptionStatus
    ? PassengerAuthenticationStateToAuthenticationStatus[subscriptionStatus]
    : AuthenticationStatus.UPCOMING

  const isPending =
    mappedStatus === AuthenticationStatus.MANUAL_PASSENGER_CHECK_IN_PENDING ||
    mappedStatus === AuthenticationStatus.MANUAL_NO_SHOW_PENDING

  const showErrorNotification = useCallback(() => {
    enqueueMoiaErrorNotification(
      t('adFleetMap_toast_passengerCheckInFailed_label'),
    )
  }, [enqueueMoiaErrorNotification, t])

  const { pendingTimedOut } = usePendingTimeout({
    isPending,
    onTimeout: showErrorNotification,
  })

  const authenticationStatus =
    pendingTimedOut && isPending
      ? AuthenticationStatus.IN_PROGRESS
      : mappedStatus

  return (
    <BoardingPassengerCard
      tripId={tripId}
      vehicleId={vehicleId}
      customer={customer}
      ridePassengerInfo={ridePassengerInfo}
      waypointStop={waypointStop}
      seats={seats}
      eta={eta}
      authenticationStatus={authenticationStatus}
      isNoShow={isNoShow}
      isBoarded={isBoarded}
    />
  )
}
